import { Injectable, signal } from '@angular/core';

export type InfoMessagePart = {
  text: string;
  bold?: boolean;
};

export type InfoModalMessage = string | InfoMessagePart;

export type InfoModalState = {
  show: boolean;
  title: string;
  messages: InfoMessagePart[];
  images: string[];
};

@Injectable({providedIn: 'root'})
export class ModalInfoService {
  private readonly _state = signal<InfoModalState>({
    show: false,
    title: 'Información',
    messages: [],
    images: [],
  });

  readonly state = this._state.asReadonly();

  private pendingMessages: InfoMessagePart[] = [];
  private flushTimer: number | null = null;

  private normalizeMessages(messages: string[] | string | InfoModalMessage[]): InfoMessagePart[] {
    const arr = Array.isArray(messages) ? messages : [messages];

    return arr
      .filter(Boolean)
      .map((m) => {
        if (typeof m === 'string') {
          return { text: m };
        }
        return {
          text: String(m.text || ''),
          bold: !!m.bold,
        };
      })
      .filter((m) => !!m.text.trim());
  }

  open(title: string, messages: string[] | string | InfoModalMessage[], images: string[] | string = []): void {
    const arr = this.normalizeMessages(messages);
    const imgArr = Array.isArray(images) ? images : [images];

    this._state.set({
      show: true,
      title: title || 'Información',
      messages: arr,
      images: imgArr.filter(Boolean).map((m) => String(m)),
    });
  }

  enqueue(title: string, messages: string[] | string | InfoModalMessage[], debounceMs: number = 100): void {
    const arr = this.normalizeMessages(messages);
    this.pendingMessages.push(...arr);

    if (this.flushTimer != null) return;

    this.flushTimer = window.setTimeout(() => {
      const unique = Array.from(
        new Map(
          this.pendingMessages.map((m) => [m.text.trim(), m])
        ).values()
      );

      this.pendingMessages = [];
      this.flushTimer = null;

      if (unique.length) {
        this.open(title, unique);
      }
    }, debounceMs);
  }

  close(): void {
    this._state.update((s) => ({...s, show: false}));
  }
}
